/**
 * @name: Recently Viewed Products
 * @author: Cursor (Agent)
 * @date: 2026-10-05
 * @description: localStorage history, same-section search fetch by handle, Liquid fallback cards, Slick slider
 * @strict-mode: true
 */

window._theme = window._theme || {};

if (typeof window._theme.alignRecommendationSlider !== 'function') {
  /**
   * @param {object} slick
   * @returns {void}
   */
  window._theme.alignRecommendationSlider = (slick) => {
    if (!slick?.$slideTrack?.length) return;

    const slidesToShow = Number(slick.options.slidesToShow) || 1;

    if (slick.slideCount > Math.ceil(slidesToShow)) return;

    slick.$slideTrack.css({
      transform: 'translate3d(0px, 0px, 0px)',
      marginLeft: 0,
    });
  };
}

const RECENTLY_VIEWED_KEY = 'recently_viewed';
const RECENTLY_VIEWED_MAX_STORED = 12;
const HANDLE_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

/** @type {string[]} */
let recentlyViewedMemory = [];

/**
 * @param {unknown} handle
 * @returns {string}
 */
const normalizeProductHandle = (handle) => {
  if (typeof handle !== 'string') return '';

  try {
    return decodeURIComponent(handle).trim().toLowerCase();
  } catch (error) {
    return handle.trim().toLowerCase();
  }
};

/**
 * @param {unknown} handle
 * @returns {boolean}
 */
const isValidProductHandle = (handle) => {
  return typeof handle === 'string' && handle.length < 255 && HANDLE_PATTERN.test(handle);
};

/**
 * @param {unknown} handles
 * @returns {string[]}
 */
const sanitizeProductHandles = (handles) => {
  if (!Array.isArray(handles)) return [];

  /** @type {string[]} */
  const unique = [];

  for (const handle of handles) {
    const normalized = normalizeProductHandle(handle);

    if (isValidProductHandle(normalized) && !unique.includes(normalized)) {
      unique.push(normalized);
    }
  }

  return unique.slice(0, RECENTLY_VIEWED_MAX_STORED);
};

/**
 * @returns {string}
 */
const getStoreRoot = () => {
  return window.Shopify?.routes?.root || '/';
};

/**
 * @param {string} [preferred]
 * @returns {string}
 */
const getCurrentProductHandle = (preferred = '') => {
  const fromPreferred = normalizeProductHandle(preferred);

  if (isValidProductHandle(fromPreferred)) {
    return fromPreferred;
  }

  const analyticsHandle = normalizeProductHandle(window.ShopifyAnalytics?.meta?.product?.handle);

  if (isValidProductHandle(analyticsHandle)) {
    return analyticsHandle;
  }

  const metaHandle = normalizeProductHandle(window.meta?.product?.handle);

  if (isValidProductHandle(metaHandle)) {
    return metaHandle;
  }

  const pathMatch = window.location.pathname.match(/\/products\/([^/?#]+)/i);

  if (!pathMatch) {
    return '';
  }

  const pathHandle = normalizeProductHandle(pathMatch[1]);

  return isValidProductHandle(pathHandle) ? pathHandle : '';
};

/**
 * @returns {string[]}
 */
const readRecentlyViewedHandles = () => {
  try {
    const raw = window.localStorage.getItem(RECENTLY_VIEWED_KEY);

    if (!raw) {
      return [...recentlyViewedMemory];
    }

    return sanitizeProductHandles(JSON.parse(raw));
  } catch (error) {
    return [...recentlyViewedMemory];
  }
};

/**
 * @param {string[]} handles
 * @returns {string[]}
 */
const writeRecentlyViewedHandles = (handles) => {
  const nextHandles = sanitizeProductHandles(handles);
  recentlyViewedMemory = nextHandles;

  try {
    window.localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(nextHandles));
  } catch (error) {
    // Private mode / quota: keep the in-memory list only.
  }

  return nextHandles;
};

/**
 * @param {string} handle
 * @returns {string[]}
 */
const recordRecentlyViewedHandle = (handle) => {
  const normalized = normalizeProductHandle(handle);

  if (!isValidProductHandle(normalized)) {
    return readRecentlyViewedHandles();
  }

  const nextHandles = [normalized, ...readRecentlyViewedHandles().filter((stored) => stored !== normalized)];

  return writeRecentlyViewedHandles(nextHandles);
};

/**
 * @param {string[]} missingHandles
 * @returns {void}
 */
const removeRecentlyViewedHandles = (missingHandles) => {
  if (!missingHandles.length) return;

  const missing = new Set(missingHandles.map(normalizeProductHandle));
  const nextHandles = readRecentlyViewedHandles().filter((handle) => !missing.has(handle));

  writeRecentlyViewedHandles(nextHandles);
};

/**
 * Records the current PDP handle on every product page, even when the section is absent.
 * @returns {string}
 */
const trackCurrentProduct = () => {
  if (!/\/products\//i.test(window.location.pathname)) {
    return '';
  }

  const handle = getCurrentProductHandle();

  if (handle) {
    recordRecentlyViewedHandle(handle);
  }

  return handle;
};

/**
 * @extends {HTMLElement}
 */
class RecentlyViewedProducts extends HTMLElement {
  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {number} */
  #requestId = 0;

  connectedCallback() {
    this.#load();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#destroySlider();
  }

  /**
   * @returns {HTMLElement|null}
   */
  #getList() {
    return this.querySelector('[data-recently-viewed-list]');
  }

  /**
   * Shows Liquid-rendered initial cards when history is empty or fetch fails.
   * @returns {boolean}
   */
  #showInitialCards() {
    const list = this.#getList();
    const initialCards = list ? [...list.querySelectorAll('.product-item[data-product-handle]')] : [];

    if (!initialCards.length) {
      this.hidden = true;
      return false;
    }

    if (this.dataset.showAtc === 'false') {
      for (const card of initialCards) {
        card.querySelector('.recom-atc-grid')?.remove();
      }
    }

    this.hidden = false;
    this.#initSlider();
    this.#initCardIntegrations();
    return true;
  }

  /**
   * @param {Element[]} cards
   * @returns {boolean}
   */
  #showCards(cards) {
    const list = this.#getList();

    if (!list || !cards.length) {
      return this.#showInitialCards();
    }

    if (this.dataset.showAtc === 'false') {
      for (const card of cards) {
        card.querySelector('.recom-atc-grid')?.remove();
      }
    }

    this.#destroySlider();
    list.replaceChildren(...cards);
    this.hidden = false;
    this.#initSlider();
    this.#initCardIntegrations();
    return true;
  }

  /**
   * Prefer localStorage history via same-section search fetch; otherwise keep Liquid fallback cards.
   * @returns {Promise<void>}
   */
  async #load() {
    const currentHandle = getCurrentProductHandle(this.dataset.productHandle);

    if (currentHandle) {
      recordRecentlyViewedHandle(currentHandle);
    }

    const limit = Math.min(Math.max(Number(this.dataset.limit) || 8, 1), RECENTLY_VIEWED_MAX_STORED);
    const displayHandles = readRecentlyViewedHandles()
      .filter((handle) => handle !== currentHandle)
      .slice(0, limit);
    const list = this.#getList();

    if (!list) {
      this.hidden = true;
      return;
    }

    // Show Liquid seed cards immediately on PDP load.
    this.#showInitialCards();

    // No prior history beyond the current product — keep initial cards.
    if (!displayHandles.length) {
      return;
    }

    const sectionId = this.dataset.sectionId || 'recently-viewed-products';

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const requestId = ++this.#requestId;

    const query = displayHandles.map((handle) => `handle:${handle}`).join(' OR ');
    const url = new URL(`${getStoreRoot()}search`, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('type', 'product');
    url.searchParams.set('q', query);

    try {
      const response = await fetch(url.toString(), {
        signal: this.#abortController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) {
        return;
      }

      if (requestId !== this.#requestId) {
        return;
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fetchedCards = [
        ...doc.querySelectorAll('[data-recently-viewed-results] .product-item[data-product-handle]'),
      ];
      const cardsByHandle = new Map();

      for (const card of fetchedCards) {
        const handle = normalizeProductHandle(card.getAttribute('data-product-handle'));

        if (isValidProductHandle(handle) && !cardsByHandle.has(handle)) {
          cardsByHandle.set(handle, card);
        }
      }

      const orderedCards = [];
      const missingHandles = [];

      for (const handle of displayHandles) {
        const card = cardsByHandle.get(handle);

        if (card) {
          orderedCards.push(card);
        } else {
          missingHandles.push(handle);
        }
      }

      removeRecentlyViewedHandles(missingHandles);

      if (!orderedCards.length) {
        return;
      }

      this.#showCards(orderedCards);
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }
      // Keep initial cards already shown.
    }
  }

  /**
   * @returns {void}
   */
  #destroySlider() {
    const slider = this.#getList();

    if (!slider || !window.jQuery) return;

    const $slider = window.jQuery(slider);

    if ($slider.hasClass('slick-initialized')) {
      $slider.slick('unslick');
    }
  }

  /**
   * @returns {void}
   */
  #initSlider() {
    const slider = this.#getList();

    if (!slider) return;

    const columns = Number(this.dataset.columns) || 5;
    const prevLabel = this.dataset.prevLabel || 'Previous';
    const nextLabel = this.dataset.nextLabel || 'Next';

    const init = () => {
      if (!window._theme || typeof window._theme.initSlickSlider !== 'function' || !window.jQuery) {
        setTimeout(init, 50);
        return;
      }

      const $slider = window.jQuery(slider);

      if ($slider.hasClass('slick-initialized')) {
        return;
      }

      window._theme.initSlickSlider(
        $slider,
        {
          rows: 0,
          dots: false,
          arrows: true,
          accessibility: true,
          infinite: false,
          mobileFirst: true,
          speed: 400,
          slidesToShow: 1.5,
          slidesToScroll: 1,
          prevArrow: `<button class="slick-prev slick-arrow" aria-label="${prevLabel}" type="button"><i class="arrow1 left1"></i><span class="sr-only">${prevLabel}</span></button>`,
          nextArrow: `<button class="slick-next slick-arrow" aria-label="${nextLabel}" type="button"><i class="arrow1 right1"></i><span class="sr-only">${nextLabel}</span></button>`,
          responsive: [
            {
              breakpoint: 767,
              settings: {
                slidesToShow: 3,
                slidesToScroll: 3,
              },
            },
            {
              breakpoint: 991,
              settings: {
                slidesToShow: columns,
                slidesToScroll: columns,
              },
            },
          ],
        },
        (slick) => {
          if (!slick || !slick.$slider) return;

          slick.$slider[0].querySelectorAll('.slick-slide').forEach((slide) => {
            slide.removeAttribute('tabindex');
          });

          if (typeof window._theme?.alignRecommendationSlider === 'function') {
            window._theme.alignRecommendationSlider(slick);
            slick.$slider
              .off('setPosition.alignLeft')
              .on('setPosition.alignLeft', () => window._theme.alignRecommendationSlider(slick));
          }
        }
      );
    };

    init();
  }

  /**
   * Re-init wishlist on cards after render.
   * Trustpilot <trustpilot-review-summary> self-inits in connectedCallback.
   * @returns {void}
   */
  #initCardIntegrations() {
    if (typeof window._theme?.initSmartWishlist === 'function') {
      window._theme.initSmartWishlist();
    }
  }
}

trackCurrentProduct();

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
